import React, { useContext } from 'react';
import IconButton, { Props as IconButtonProps } from '../../../Buttons/IconButton';
import IconButtonWithBackground from '../../../Buttons/IconButton/IconButtonWithBackground';
import { useDropdownToggleProps } from '../../hooks/useDropdownToggleProps';
import type { ComponentRequiresAriaHasPopup } from '../types';
import { DropdownStateContext } from '../../DropdownStateManager';

export type Props = ComponentRequiresAriaHasPopup &
  IconButtonProps & {
    withBackground?: boolean;
  };

/**
 * A wrapper around our DS `IconButton` that provides the props for a dropdown toggle.
 *
 * Note: This component is only the toggle! It doesn't contain a popover or dropdown. If you're looking for something that does,
 * you probably want a preset instead of this. Consider `IconButtonWithContextMenu`.
 *
 * Required contexts: `PopperDropdownContext`, `DropdownStateContext`
 */
const PopperIconToggleButton: React.FC<Props> = ({
  withBackground = false,
  tooltipText,
  ...rest
}) => {
  const necessaryToggleProps = useDropdownToggleProps();
  const { isOpen } = useContext(DropdownStateContext);

  const IconButtonComponent = withBackground ? IconButtonWithBackground : IconButton;

  return (
    <IconButtonComponent
      {...necessaryToggleProps}
      {...rest}
      // To prevent the tooltip from showing overtop our dropdown menu
      tooltipText={isOpen ? undefined : tooltipText}
    />
  );
};
PopperIconToggleButton.displayName = 'PopperIconToggleButton';

export default PopperIconToggleButton;
